import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Box, CalendarCheck, FileText, ImageUp, Link2, MessageCircle, MessageSquareText, Monitor, MousePointerClick, Package, Radar,
  ShoppingCart, Smartphone, Sparkles, Tablet, UserPlus, UserX,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, KpiCard, PillTabs, RelativeTime, formatNumber } from '../../../components/design-system/admin';
import { BarList } from '../../../components/design-system/analytics/BarList';
import { TrendChart } from '../../../components/design-system/analytics/TrendChart';
import {
  DemoAction, DemoClickKind, DemoSite, DemoVisitStats, DemoVisitSummary, apiErrorMessage, useDemoSitesApi,
} from '../../../common/hooks/use-demo-sites-api.hook';

type VisitDays = 7 | 30 | 90;

const KIND_ICON: Record<DemoClickKind, typeof Sparkles> = {
  visualizer: Sparkles,
  chat: MessageCircle,
  ar: Box,
  cart: ShoppingCart,
  book: CalendarCheck,
  signup: UserPlus,
  link: Link2,
  button: MousePointerClick,
};

const ACTION_ICON: Record<DemoAction, typeof Sparkles> = {
  visualizer_opened: Sparkles,
  room_uploaded: ImageUp,
  product_tried: Package,
  assistant_opened: MessageCircle,
  question_asked: MessageSquareText,
  booking_sent: CalendarCheck,
};

const DEVICE_ICON = { phone: Smartphone, tablet: Tablet, desktop: Monitor } as const;

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const countryName = (code?: string): string => {
  if (!code) return '';
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
};

const duration = (ms?: number): string => {
  const s = Math.round((ms || 0) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
};

/** In the demos list: visitors and clicks at a glance, or why there are none. */
export function VisitsCell({ site, summary, onOpen }: { site: DemoSite; summary?: DemoVisitSummary; onOpen: () => void }) {
  const { t } = useTranslation();
  if (!site.tracking && !summary) {
    return (
      <button type="button" onClick={onOpen} className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline" data-testid={`visits-${site.slug}`}>
        {t('SUPER_ADMIN.DEMO_SITES.VISITS.NOT_TRACKED')}
      </button>
    );
  }
  return (
    <button type="button" onClick={onOpen} className="group text-start" data-testid={`visits-${site.slug}`}>
      <div className="font-medium tabular-nums group-hover:underline">
        {t('SUPER_ADMIN.DEMO_SITES.VISITS.VISITORS', { count: summary?.visitors ?? 0 })}
      </div>
      <div className="text-xs text-muted-foreground">
        {summary?.lastAt ? (
          <>
            {t('SUPER_ADMIN.DEMO_SITES.VISITS.CLICKS', { count: summary.clicks })} · <RelativeTime value={summary.lastAt} />
          </>
        ) : (
          t('SUPER_ADMIN.DEMO_SITES.VISITS.NONE_YET')
        )}
      </div>
    </button>
  );
}

/**
 * Who visited one demo and what they clicked: visitors, visits that clicked,
 * opens of our visualizer, time on the page; which pages of the site were seen
 * (its home page, then any other by its path: /overview.html…); what is clicked most; where
 * visitors come from; and each visit, page by page and click by click. The
 * Ardealy team's own visits are left out of every number, and the sheet says
 * how many.
 */
export function DemoVisitsSheet({ site, onClose, onTrackingEnabled }: { site: DemoSite | null; onClose: () => void; onTrackingEnabled?: () => void }) {
  const { t } = useTranslation();
  const api = useDemoSitesApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [days, setDays] = useState<VisitDays>(30);
  const [stats, setStats] = useState<DemoVisitStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const load = useCallback(async () => {
    if (!site) return;
    setLoading(true);
    try {
      setStats(await apiRef.current.visits(site.slug, days));
    } catch {
      setStats(null);
      toast.error(t('SUPER_ADMIN.DEMO_SITES.VISITS.LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.slug, days]);

  useEffect(() => {
    setStats(null);
  }, [site?.slug]);

  useEffect(() => {
    load();
  }, [load]);

  const enable = async () => {
    if (!site) return;
    setEnabling(true);
    try {
      const res = await apiRef.current.enableTracking(site.slug);
      toast.success(t('SUPER_ADMIN.DEMO_SITES.VISITS.TRACKING_ON', { count: res.updated }));
      onTrackingEnabled?.();
      load();
    } catch (e) {
      toast.error((await apiErrorMessage(e)) || t('SUPER_ADMIN.DEMO_SITES.VISITS.TRACKING_FAILED'));
    } finally {
      setEnabling(false);
    }
  };

  const tracked = Boolean(site?.tracking || stats?.tracking);
  const firstVisitAt = stats?.firstVisitAt || site?.firstVisitAt || null;
  const s = stats?.totals;
  const kindLabel = (kind: DemoClickKind) => t(`SUPER_ADMIN.DEMO_SITES.VISITS.KIND.${kind.toUpperCase()}`);
  const actionLabel = (action: DemoAction) => t(`SUPER_ADMIN.DEMO_SITES.VISITS.ACTION.${action.toUpperCase()}`);
  /** "/" is the demo itself; any other page of the site keeps its path (/overview.html, /tarifs.html). */
  const pageLabel = (page?: string) => (!page || page === '/' ? t('SUPER_ADMIN.DEMO_SITES.VISITS.PAGE_HOME') : page);

  return (
    <Sheet open={!!site} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full gap-0 p-0 text-sm sm:max-w-[640px]" data-testid="demo-visits-sheet">
        {site && (
          <>
            <SheetHeader className="gap-1 border-b p-6 pe-12">
              <SheetTitle className="min-w-0 truncate font-heading text-xl font-medium tracking-tight" dir="ltr">
                {site.slug}.ardealy.link
              </SheetTitle>
              <SheetDescription className="text-sm">
                {site.vendor?.name || site.title || t('SUPER_ADMIN.DEMO_SITES.VISITS.TITLE')} · {t('SUPER_ADMIN.DEMO_SITES.VISITS.TEAM_NOTE')}
              </SheetDescription>
              {tracked && (
                <p className="text-xs text-muted-foreground" data-testid="demo-visits-first">
                  {firstVisitAt
                    ? t('SUPER_ADMIN.DEMO_SITES.VISITS.FIRST_VISIT', { date: day(firstVisitAt) })
                    : t('SUPER_ADMIN.DEMO_SITES.VISITS.NOT_OPENED')}
                </p>
              )}
            </SheetHeader>

            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6 [&>*]:shrink-0">
              {!tracked && (
                <div className="flex flex-col gap-3 rounded-lg border border-dashed p-4" data-testid="demo-visits-untracked">
                  <div className="flex items-start gap-3">
                    <Radar className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <div className="grid gap-1">
                      <div className="font-medium">{t('SUPER_ADMIN.DEMO_SITES.VISITS.UNTRACKED_TITLE')}</div>
                      <p className="text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.UNTRACKED_BODY')}</p>
                    </div>
                  </div>
                  <div>
                    <Button size="sm" onClick={enable} disabled={enabling} data-testid="demo-visits-enable">
                      {enabling ? t('SUPER_ADMIN.DEMO_SITES.VISITS.ENABLING') : t('SUPER_ADMIN.DEMO_SITES.VISITS.ENABLE')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Before tracking, there is nothing to count: only the way to turn it on. */}
              {(tracked || (stats?.totals.views ?? 0) > 0) && (
                <>
                  <div className="flex justify-end">
                    <PillTabs
                      value={days}
                      onChange={setDays}
                      ariaLabel={t('SUPER_ADMIN.DEMO_SITES.VISITS.PERIOD')}
                      options={[
                        { value: 7 as VisitDays, label: t('SUPER_ADMIN.DEMO_SITES.VISITS.D7') },
                        { value: 30 as VisitDays, label: t('SUPER_ADMIN.DEMO_SITES.VISITS.D30') },
                        { value: 90 as VisitDays, label: t('SUPER_ADMIN.DEMO_SITES.VISITS.D90') },
                      ]}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <KpiCard
                      label={t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_VISITORS')}
                      value={formatNumber(s?.visitors)}
                      hint={s ? t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_VISITORS_HINT', { visits: s.sessions, returning: s.returning }) : undefined}
                      loading={loading && !stats}
                      testId="demo-kpi-visitors"
                    />
                    <KpiCard
                      label={t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_ENGAGED')}
                      value={s?.sessions ? `${Math.round((100 * s.clickedSessions) / s.sessions)}%` : '—'}
                      hint={s ? t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_ENGAGED_HINT', { count: s.clicks }) : undefined}
                      loading={loading && !stats}
                    />
                    <KpiCard
                      label={t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_VISUALIZER')}
                      value={formatNumber(s?.visualizer)}
                      hint={s ? t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_VISUALIZER_HINT', { rooms: s.roomUploads ?? 0, products: s.productsTried ?? 0 }) : undefined}
                      loading={loading && !stats}
                      testId="demo-kpi-visualizer"
                    />
                    <KpiCard
                      label={t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_ASSISTANT')}
                      value={formatNumber(s?.assistant)}
                      hint={s ? t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_ASSISTANT_HINT', { count: s.questions ?? 0 }) : undefined}
                      loading={loading && !stats}
                      testId="demo-kpi-assistant"
                    />
                    <KpiCard
                      label={t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_BOOK')}
                      value={formatNumber(s?.bookClicks)}
                      hint={s ? t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_BOOK_HINT', { bookings: s.bookings ?? 0, signups: s.signupClicks ?? 0 }) : undefined}
                      loading={loading && !stats}
                      testId="demo-kpi-book"
                    />
                    <KpiCard
                      label={t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_TIME')}
                      value={s?.avgSeconds ? duration(s.avgSeconds * 1000) : '—'}
                      hint={[
                        s?.avgScroll ? t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_SCROLL', { pct: s.avgScroll }) : null,
                        s?.cart ? t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_CART', { count: s.cart }) : null,
                      ].filter(Boolean).join(' · ') || undefined}
                      loading={loading && !stats}
                    />
                  </div>

                  {stats && stats.excluded.views + stats.excluded.clicks > 0 && (
                    <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="demo-visits-excluded">
                      <UserX className="size-3.5" />
                      {t('SUPER_ADMIN.DEMO_SITES.VISITS.EXCLUDED', {
                        views: stats.excluded.views,
                        clicks: stats.excluded.clicks,
                        people: stats.excluded.people.join(', ') || '—',
                      })}
                    </p>
                  )}

                  {loading && !stats ? (
                    <Skeleton className="h-[180px] w-full" />
                  ) : stats && stats.totals.views === 0 ? (
                    <EmptyState
                      icon={<MousePointerClick />}
                      title={t('SUPER_ADMIN.DEMO_SITES.VISITS.EMPTY_TITLE')}
                      description={t(tracked ? 'SUPER_ADMIN.DEMO_SITES.VISITS.EMPTY_BODY' : 'SUPER_ADMIN.DEMO_SITES.VISITS.EMPTY_UNTRACKED')}
                    />
                  ) : stats ? (
                    <>
                      <section className="grid gap-2">
                        <h3 className="text-xs font-medium text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.TREND')}</h3>
                        <TrendChart
                          data={stats.daily.map((d) => ({ label: d.day, value: d.visitors }))}
                          valueLabel={t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_VISITORS')}
                          height={160}
                          partialLast
                          partialLabel={t('SUPER_ADMIN.DEMO_SITES.VISITS.SO_FAR')}
                          formatLabel={(d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                          formatValue={(v) => formatNumber(v)}
                        />
                      </section>

                      <section className="grid gap-2" data-testid="demo-visits-pages">
                        <h3 className="text-xs font-medium text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.PAGES')}</h3>
                        <BarList
                          items={(stats.pages || []).map((p) => ({
                            key: p.page,
                            leading: <FileText className="size-3.5 text-muted-foreground" />,
                            label: <span dir="ltr">{pageLabel(p.page)}</span>,
                            value: p.visitors,
                            secondary: t('SUPER_ADMIN.DEMO_SITES.VISITS.PAGE_VIEWS', { count: p.views }),
                          }))}
                          format={(v) => formatNumber(v)}
                        />
                      </section>

                      <section className="grid gap-2" data-testid="demo-visits-clicks">
                        <h3 className="text-xs font-medium text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.TOP_CLICKS')}</h3>
                        <BarList
                          items={stats.clicks.map((c, i) => {
                            const Icon = KIND_ICON[c.kind] || MousePointerClick;
                            return {
                              key: `${c.kind}-${c.label || ''}-${i}`,
                              leading: <Icon className="size-3.5 text-muted-foreground" />,
                              label: c.label || kindLabel(c.kind),
                              value: c.count,
                              secondary: `${kindLabel(c.kind)} · ${t('SUPER_ADMIN.DEMO_SITES.VISITS.VISITORS', { count: c.visitors })}`,
                            };
                          })}
                          format={(v) => formatNumber(v)}
                          emptyText={t('SUPER_ADMIN.DEMO_SITES.VISITS.NO_CLICKS')}
                        />
                      </section>

                      <section className="grid gap-2" data-testid="demo-visits-products">
                        <h3 className="text-xs font-medium text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.PRODUCTS')}</h3>
                        <BarList
                          items={(stats.products || []).map((p) => ({
                            key: p.label,
                            leading: <Package className="size-3.5 text-muted-foreground" />,
                            label: p.label,
                            value: p.count,
                            secondary: t('SUPER_ADMIN.DEMO_SITES.VISITS.VISITORS', { count: p.visitors }),
                          }))}
                          format={(v) => formatNumber(v)}
                          emptyText={t('SUPER_ADMIN.DEMO_SITES.VISITS.NO_PRODUCTS')}
                        />
                      </section>

                      <div className="grid gap-6 sm:grid-cols-2">
                        <section className="grid content-start gap-2">
                          <h3 className="text-xs font-medium text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.SOURCES')}</h3>
                          <BarList
                            items={stats.referrers.map((r) => ({
                              key: r.referrer || 'direct',
                              label: r.referrer || t('SUPER_ADMIN.DEMO_SITES.VISITS.DIRECT'),
                              value: r.visitors,
                            }))}
                            format={(v) => formatNumber(v)}
                          />
                        </section>
                        <section className="grid content-start gap-2">
                          <h3 className="text-xs font-medium text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.WHERE')}</h3>
                          <BarList
                            items={[
                              ...stats.devices.map((d) => ({ key: d.device, label: t(`SUPER_ADMIN.DEMO_SITES.VISITS.DEVICE.${d.device.toUpperCase()}`), value: d.visitors })),
                              ...stats.countries.map((c) => ({ key: `c-${c.country}`, label: countryName(c.country), value: c.visitors })),
                            ]}
                            format={(v) => formatNumber(v)}
                          />
                        </section>
                      </div>

                      <section className="grid gap-2" data-testid="demo-visits-sessions">
                        <h3 className="text-xs font-medium text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.SESSIONS')}</h3>
                        <ol className="grid gap-2">
                          {stats.sessions.map((v) => {
                            const Device = DEVICE_ICON[v.device || 'desktop'];
                            // On a visit across several pages, each chip says which page it happened on.
                            const onPage = (page?: string) => ((v.pages?.length || 0) > 1 ? ` · ${pageLabel(page)}` : '');
                            return (
                              <li key={v.session} className="grid gap-2 rounded-lg border p-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="inline-flex items-center gap-2">
                                    <Device className="size-3.5 text-muted-foreground" />
                                    <RelativeTime value={v.startedAt} />
                                    <span className="text-muted-foreground">
                                      {[countryName(v.country), v.referrer, v.durationMs ? duration(v.durationMs) : null].filter(Boolean).join(' · ')}
                                    </span>
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {t('SUPER_ADMIN.DEMO_SITES.VISITS.VISITOR_ID', { id: v.visitor.slice(0, 6) })}
                                  </span>
                                </div>
                                {v.pages?.length ? (
                                  <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground" data-testid="demo-visit-pages">
                                    <FileText className="size-3 shrink-0" />
                                    <span className="truncate" dir="ltr">{v.pages.map(pageLabel).join(' → ')}</span>
                                  </p>
                                ) : null}
                                {v.clicks.length + (v.actions?.length || 0) > 0 ? (
                                  <ul className="flex flex-wrap gap-1.5">
                                    {/* Clicks and what was done in our frames, in the order they happened. */}
                                    {[
                                      ...v.clicks.map((c) => ({
                                        at: c.at,
                                        Icon: KIND_ICON[c.kind] || MousePointerClick,
                                        text: c.label || kindLabel(c.kind),
                                        title: kindLabel(c.kind) + onPage(c.page),
                                      })),
                                      ...(v.actions || []).map((a) => ({
                                        at: a.at,
                                        Icon: ACTION_ICON[a.action] || Sparkles,
                                        text: a.label ? `${actionLabel(a.action)}: ${a.label}` : actionLabel(a.action),
                                        title: actionLabel(a.action) + onPage(a.page),
                                      })),
                                    ]
                                      .sort((a, b) => String(a.at).localeCompare(String(b.at)))
                                      .map(({ Icon, text, title }, i) => (
                                        <li key={i} title={title} className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs">
                                          <Icon className="size-3 shrink-0 text-muted-foreground" />
                                          <span className="truncate">{text}</span>
                                        </li>
                                      ))}
                                  </ul>
                                ) : (
                                  <p className="text-xs text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.LOOKED_ONLY')}</p>
                                )}
                              </li>
                            );
                          })}
                        </ol>
                      </section>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * A vendor's demos as its CRM lead shows them: for each demo the prospect
 * visited, who came, what they did in the visualizer and the assistant, and
 * when — the Ardealy team's own visits left out. Opens the demo's visits.
 */
export function VendorDemoVisits({ vendorId }: { vendorId: string }) {
  const { t } = useTranslation();
  const api = useDemoSitesApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [rows, setRows] = useState<[string, DemoVisitSummary][] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setRows(null);
    setFailed(false);
    apiRef.current
      .visitSummary(vendorId)
      .then((summary) => live && setRows(Object.entries(summary || {}).sort((a, b) => String(b[1].lastAt).localeCompare(String(a[1].lastAt)))))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [vendorId]);

  return (
    <div className="mt-2 flex flex-col gap-1.5" data-testid="crm-lead-demo-visits">
      <span className="text-xs text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.LEAD_TITLE')}</span>
      {failed ? (
        <p className="text-xs text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.LEAD_FAILED')}</p>
      ) : !rows ? (
        <Skeleton className="h-12 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.LEAD_NONE')}</p>
      ) : (
        rows.map(([slug, v]) => (
          <Link
            key={slug}
            to={`/demo-sites?visits=${encodeURIComponent(slug)}`}
            className="flex min-w-0 flex-col gap-0.5 rounded-lg border px-3 py-2 hover:bg-muted/50"
            data-testid={`crm-demo-visits-${slug}`}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="truncate text-sm font-medium" dir="ltr">{slug}.ardealy.link</span>
              {v.lastAt && (
                <span className="shrink-0 text-xs text-muted-foreground">
                  <RelativeTime value={v.lastAt} />
                </span>
              )}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('SUPER_ADMIN.DEMO_SITES.VISITS.LEAD_LINE', { visitors: v.visitors, visualizer: v.visualizer ?? 0, questions: v.questions ?? 0 })}
              {v.bookClicks || v.bookings ? ` · ${t('SUPER_ADMIN.DEMO_SITES.VISITS.KPI_BOOK_HINT', { bookings: v.bookings ?? 0, signups: v.signupClicks ?? 0 })}` : ''}
            </span>
            {v.firstAt && (
              <span className="text-xs text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.VISITS.FIRST_VISIT', { date: day(v.firstAt) })}</span>
            )}
          </Link>
        ))
      )}
    </div>
  );
}
